import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserModal.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export default function UserModal({ user, onClose, onSave }) {
  const [form, setForm] = useState({ 
    ...user, 
    firstName: user.firstName || '', 
    lastName: user.lastName || '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  // Debounced email validation to check for duplicates
  useEffect(() => {
    if (form.email && touched.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      const timeoutId = setTimeout(async () => {
        try {
          const response = await axios.get(`${API_BASE}/admin/users/check-email/${encodeURIComponent(form.email)}`);
          if (response.data.exists && (!user._id || user.email !== form.email)) {
            setErrors(prev => ({ ...prev, email: 'Email already exists with an active account' }));
          } else {
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.email;
              return newErrors;
            });
          }
        } catch (err) {
          // Ignore check errors, let server handle validation on submit
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [form.email, touched.email, user._id, user.email]);

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    
    switch (name) {
      case 'firstName':
        if (!value.trim()) {
          newErrors.firstName = 'First name is required';
        } else if (value.trim().length < 2) {
          newErrors.firstName = 'First name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s\-']+$/.test(value)) {
          newErrors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
        } else {
          delete newErrors.firstName;
        }
        break;
        
      case 'lastName':
        if (!value.trim()) {
          newErrors.lastName = 'Last name is required';
        } else if (value.trim().length < 2) {
          newErrors.lastName = 'Last name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s\-']+$/.test(value)) {
          newErrors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
        } else {
          delete newErrors.lastName;
        }
        break;
        
      case 'email':
        if (!value.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Please provide a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
        
      case 'password':
        if (!value) {
          newErrors.password = 'Password is required';
        } else if (value.length < 6) {
          newErrors.password = 'Password must be at least 6 characters';
        } else {
          delete newErrors.password;
          // Re-validate confirm password if it exists
          if (form.confirmPassword) {
            validateField('confirmPassword', form.confirmPassword);
          }
        }
        break;
        
      case 'confirmPassword':
        if (!value) {
          newErrors.confirmPassword = 'Please confirm your password';
        } else if (value !== form.password) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          delete newErrors.confirmPassword;
        }
        break;
        
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    
    // Clear any existing errors immediately when user starts typing
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name];
      delete newErrors.general; // Also clear general errors
      return newErrors;
    });
    
    // Validate field on change if it's been touched
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = field => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, form[field]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(form).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);
    
    // Validate all fields
    let isValid = true;
    Object.keys(form).forEach(field => {
      if (!validateField(field, form[field])) {
        isValid = false;
      }
    });
    
    if (!isValid) return;
    
    setSaving(true);
    try {
      if (!form._id) {
        // For new staff, send verification email first
        await axios.post(`${API_BASE}/auth/send-verification`, {
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName
        });
        setShowVerification(true);
      } else {
        // For existing staff, save directly
        await onSave(form);
      }
    } catch (err) {
      setErrors({
        general: err.response?.data?.message || 'Failed to create staff account'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyEmail = async () => {
    setVerifying(true);
    setVerificationError('');
    
    try {
      await axios.post(`${API_BASE}/auth/verify-email`, {
        email: form.email,
        code: verificationCode
      });
      
      setVerified(true);
      
      // Now create the staff account
      setTimeout(async () => {
        try {
          const staffData = {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            password: form.password,
            role: 'staff'
          };
          
          await onSave(staffData);
          setShowVerification(false);
        } catch (err) {
          setErrors({
            general: 'Email verified but failed to create staff account'
          });
        }
      }, 1000);
      
    } catch (err) {
      setVerificationError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="user-modal-overlay" onClick={onClose}>
      <div className="user-modal-content" onClick={e => e.stopPropagation()}>
        <div className="user-modal-icon">👥</div>
        <h3>{form._id ? 'Edit Staff Member' : 'Add New Staff Member'}</h3>
        <p>Enter staff member details to create their account</p>

        <form onSubmit={handleSubmit}>
          <div className="user-field-group">
            <input
              name="firstName"
              type="text"
              placeholder=" "
              value={form.firstName}
              onChange={handleChange}
              onBlur={() => handleBlur('firstName')}
              className={touched.firstName && errors.firstName ? 'has-error' : ''}
              required
              maxLength={20}
            />
            <label htmlFor="firstName">First Name</label>
            <small className="user-error">{touched.firstName && errors.firstName ? errors.firstName : ' '}</small>
          </div>

          <div className="user-field-group">
            <input
              name="lastName"
              type="text"
              placeholder=" "
              value={form.lastName}
              onChange={handleChange}
              onBlur={() => handleBlur('lastName')}
              className={touched.lastName && errors.lastName ? 'has-error' : ''}
              required
              maxLength={20}
            />
            <label htmlFor="lastName">Last Name</label>
            <small className="user-error">{touched.lastName && errors.lastName ? errors.lastName : ' '}</small>
          </div>

          <div className="user-field-group">
            <input
              name="email"
              type="email"
              placeholder=" "
              value={form.email}
              onChange={handleChange}
              onBlur={() => handleBlur('email')}
              className={touched.email && errors.email ? 'has-error' : ''}
              required
              maxLength={50}
            />
            <label htmlFor="email">Email Address</label>
            <small className="user-error">{touched.email && errors.email ? errors.email : ' '}</small>
          </div>

          {!form._id && (
            <>
              <div className="user-field-group">
                <input
                  name="password"
                  type="password"
                  placeholder=" "
                  value={form.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('password')}
                  className={touched.password && errors.password ? 'has-error' : ''}
                  required
                  maxLength={20}
                />
                <label htmlFor="password">Password</label>
                <small className="user-error">{touched.password && errors.password ? errors.password : ' '}</small>
              </div>

              <div className="user-field-group">
                <input
                  name="confirmPassword"
                  type="password"
                  placeholder=" "
                  value={form.confirmPassword}
                  onChange={handleChange}
                  onBlur={() => handleBlur('confirmPassword')}
                  className={touched.confirmPassword && errors.confirmPassword ? 'has-error' : ''}
                  required
                  maxLength={20}
                />
                <label htmlFor="confirmPassword">Confirm Password</label>
                <small className="user-error">{touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : ' '}</small>
              </div>
            </>
          )}

          {errors.general && (
            <div className="user-alert user-alert-error" style={{ marginBottom: '1.5rem' }}>
              <span>⚠️</span>
              {errors.general}
            </div>
          )}

          <div className="user-modal-actions">
            <button
              type="button"
              className="user-btn-cancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="user-btn-primary"
              disabled={saving}
            >
              {saving ? 'Creating...' : (form._id ? 'Update Staff' : 'Create Staff')}
            </button>
          </div>
        </form>

        {/* Email Verification Modal */}
        {showVerification && (
          <div className="user-modal-overlay" style={{ zIndex: 2000 }}>
            <div className="user-modal-content" onClick={e => e.stopPropagation()}>
              <div className="user-modal-icon">✉️</div>
              <h3>Email Verification</h3>
              <p>Please enter the verification code sent to <strong>{form.email}</strong></p>
              
              <div className="user-field-group">
                <input
                  type="text"
                  placeholder=" "
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  style={{ 
                    textAlign: 'center', 
                    fontSize: '1.2rem', 
                    letterSpacing: '0.3rem',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    width: '100%'
                  }}
                  maxLength={6}
                />
                <label>Verification Code</label>
                <small className="user-error"></small>
              </div>
              
              {verificationError && (
                <div className="user-alert user-alert-error" style={{ marginBottom: '1.5rem' }}>
                  <span>⚠️</span>
                  {verificationError}
                </div>
              )}
              
              {verified && (
                <div className="user-alert" style={{ 
                  background: '#f0fdf4', 
                  color: '#16a34a', 
                  border: '1px solid #bbf7d0',
                  marginBottom: '1.5rem'
                }}>
                  <span>✅</span>
                  Email verified successfully! Creating staff account...
                </div>
              )}
              
              <div className="user-modal-actions">
                <button
                  type="button"
                  className="user-btn-cancel"
                  onClick={() => {
                    setShowVerification(false);
                    setVerificationCode('');
                    setVerificationError('');
                    setVerified(false);
                  }}
                  disabled={verifying || verified}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="user-btn-primary"
                  onClick={handleVerifyEmail}
                  disabled={verifying || verified || verificationCode.length !== 6}
                >
                  {verifying ? 'Verifying...' : verified ? '✓ Verified' : 'Verify Code'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
